/**
 * @file        src/pages/flight/MarkupList.jsx
 * @description B2C Flight Markup (CRUD) over margin_rules (flight-scoped), shaped
 *              to the legacy console: Dom/Int, Amount Type (Fix/Percentage),
 *              Amount, Markup Type (ALL/Airline Wise), Airline Code, Fare Type.
 *              These map to margin_rules columns: dom_int, margin_type,
 *              margin_value, level, airline_code, fare_type. The booking engine
 *              (marginRuleService) applies a rule to a live fare, gating by
 *              dom/int + fare identifier.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (B2C Flight Management).
 *   - 2026-07-08 Claude — Reshaped to the legacy Flight Markup fields (Dom/Int,
 *                          Amount Type, Amount, Markup Type, Airline Code, Fare
 *                          Type) now that margin_rules carries dom_int/fare_type.
 *   - 2026-08-03 Claude — Required Brand field (registry code, no default) —
 *                          the API now demands `brand` on create.
 *   - 2026-09-12 Claude — Precedence notice. Several rules can match one fare
 *                          (a blanket ALL rule and an Airline Wise one), and the
 *                          screen gave no clue which applied — the engine used to
 *                          pick the LARGEST amount, so a newly added rule looked
 *                          ignored. The engine now picks the most specific match
 *                          and the API rejects an identical-scope duplicate; this
 *                          line states both.
 *   - 2026-09-15 Claude — Note now also states that the markup is already inside
 *                          the displayed price, so the convenience fee, discounts
 *                          and coupons are all computed on the marked-up total —
 *                          never on the supplier net.
 *   - 2026-10-05 Claude — Fare Type lists the fare families the storefront shows
 *                          on View Prices (Published, Lite, Sale, NDC Xpress
 *                          Value …) from lib/fareTypes, named as the customer sees
 *                          them, plus Other… for a family not listed. The old list
 *                          used names the site never prints, and `COUPON`, which
 *                          no live fare carries (TripJack says COUPONFARE).
 */
import SimpleLookupPage from '../package/SimpleLookupPage';
import { MARKUP_BASE_NOTE } from '../../lib/pricingNotes';
import { FLIGHT_FARE_TYPES } from '../../lib/fareTypes';

// Amount Type — legacy "Fix" is margin_rules' 'fixed'.
const AMOUNT_TYPE = [
  { value: 'fixed', label: 'Fix' },
  { value: 'percentage', label: 'Percentage' },
];
// Markup Type — ALL = every airline (level 'global'), Airline Wise = one airline.
const MARKUP_TYPE = [
  { value: 'global', label: 'ALL' },
  { value: 'airline', label: 'Airline Wise' },
];
const DOM_INT = [
  { value: 'domestic', label: 'Domestic' },
  { value: 'international', label: 'International' },
];

export default function MarkupList() {
  return (
    <SimpleLookupPage
      title="B2C Flight Markup"
      subtitle="B2C Flight · fare markup rules"
      endpoint="/flight-markups"
      queryRoot="flight-markups"
      addLabel="Add Flight Markup"
      entityLabel="Markup"
      listParams={{ audience: 'b2c' }}
      notice={<span>Only <b>one</b> markup applies to a fare: the most specific match wins (Airline&nbsp;Wise beats ALL, and a Fare&nbsp;Type-specific rule beats one with no fare type). A second <b>active</b> rule with exactly the same Brand + Dom/Int + Markup&nbsp;Type + Airline&nbsp;Code + Fare&nbsp;Type is rejected — edit the existing rule instead.{MARKUP_BASE_NOTE}</span>}
      fields={[
        { key: 'brand', label: 'Brand', type: 'brand', required: true },
        { key: 'dom_int', label: 'Dom/Int', type: 'select', default: 'domestic', options: DOM_INT },
        { key: 'margin_type', label: 'Amount Type', type: 'select', required: true, default: 'fixed', options: AMOUNT_TYPE },
        { key: 'margin_value', label: 'Amount', type: 'number', required: true, min: 0, step: '0.01' },
        { key: 'level', label: 'Markup Type', type: 'select', default: 'global', options: MARKUP_TYPE },
        { key: 'airline_code', label: 'Airline Code', type: 'text', maxLength: 10, placeholder: 'e.g. 6E / AI (Airline Wise only)' },
        { key: 'fare_type', label: 'Fare Type', type: 'select', options: FLIGHT_FARE_TYPES, allowOther: true, maxLength: 30, otherPlaceholder: 'Fare type as shown on the site, e.g. Xpress Lite' },
      ]}
    />
  );
}
